import { clearAuthData } from "./authService";

// Constants
const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const SESSION_STORAGE_KEY = "session_start";
const LAST_ACTIVITY_KEY = "last_activity";

let idleTimer = null;

// Check if session is expired
const checkSession = () => {
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  if (!lastActivity) return true;

  const now = Date.now();
  const idle = now - parseInt(lastActivity, 10);
  return idle > IDLE_TIMEOUT;
};

// Update last activity timestamp
const updateActivity = () => {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

// Handle different types of logouts
const handleLogout = (reason = "manual") => {
  console.warn("Session logout triggered but temporarily disabled for debugging:", reason);
  // Temporarily disable logout for debugging
  // clearTimeout(idleTimer);
  // clearAuthData();
  // sessionStorage.clear();
  // window.location.href = `/sign-in?reason=${reason}`;
};

// Initialize session management
export const initializeSession = () => {
  // Check if we have an existing session
  const sessionStart = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionStart) {
    // Start a new session instead of logging out
    startSession();
    return;
  }

  // If session exists but is expired due to inactivity
  if (checkSession()) {
    handleLogout("idle");
    return;
  }

  // Set up periodic session check
  idleTimer = setInterval(() => {
    if (checkSession()) {
      handleLogout("idle");
    }
  }, 60000); // Check every minute

  // Update activity timestamp
  updateActivity();

  // Add page visibility change handler
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Setup activity tracking
  setupActivityTracking();
};

// Handle visibility change
const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    // When page becomes visible, check session
    if (checkSession()) {
      handleLogout("idle");
    } else {
      // If not expired, update the activity timestamp
      updateActivity();
    }
  }
};

// Setup tracking for user activity
const setupActivityTracking = () => {
  // List of events to track for user activity
  const activityEvents = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "touchstart",
    "click",
    "keypress",
  ];

  // Add event listeners for all activity events
  activityEvents.forEach((eventName) => {
    window.addEventListener(eventName, updateActivity, { passive: true });
  });
};

// Remove activity tracking
const removeActivityTracking = () => {
  const activityEvents = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "touchstart",
    "click",
    "keypress",
  ];

  activityEvents.forEach((eventName) => {
    window.removeEventListener(eventName, updateActivity);
  });
};

// Start new session
export const startSession = () => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
  updateActivity();

  // Clear any existing timers
  clearTimeout(idleTimer);

  // Set up new timer
  idleTimer = setInterval(() => {
    if (checkSession()) {
      handleLogout("idle");
    }
  }, 60000); // Check every minute

  // Setup activity tracking
  setupActivityTracking();

  // Add page visibility change handler if not already added
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  document.addEventListener("visibilitychange", handleVisibilityChange);
};

// Cleanup session
export const cleanupSession = () => {
  clearTimeout(idleTimer);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  removeActivityTracking();
};

// End session manually
export const endSession = () => {
  cleanupSession();
  handleLogout("manual");
};

// Export for direct usage
export { handleLogout };
